import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart3, Building2, Edit, Plus, Settings, Trash2, Users, XCircle } from 'lucide-react';

// User Management Component (Super Admin Only)
const UserManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyBalances, setAgencyBalances] = useState({}); // Store balances for all agencies
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'agency_staff',
    agency_id: ''
  });

  // Check if current user is Super Admin
  if (currentUser?.role !== 'super_admin') {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-800 mb-2">غير مسموح بالوصول</h3>
            <p className="text-red-600">هذا القسم مخصص للمدير العام فقط</p>
          </div>
        </div>
      </div>
    );
  }

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, agenciesResponse] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/agencies`)
      ]);
      
      // Sort users by creation date - newest first
      const sortedUsers = usersResponse.data.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      // Sort agencies by creation date - newest first
      const sortedAgencies = agenciesResponse.data.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setUsers(sortedUsers);
      setAgencies(sortedAgencies);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
      } else {
        // Create new user
        await axios.post(`${API}/users`, formData);
      }
      
      setFormData({ name: '', email: '', password: '', role: 'agency_staff', agency_id: '' });
      setEditingUser(null);
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.detail || 'حدث خطأ أثناء حفظ المستخدم');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Don't populate password
      role: user.role,
      agency_id: user.agency_id
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error.response?.data?.detail || 'حدث خطأ أثناء حذف المستخدم');
      }
    }
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      'super_admin': '👑 مدير عام',
      'general_accountant': '💼 محاسب عام',
      'services_manager': '📦 مسؤول الخدمات',
      'general_manager': '🎯 مسؤول عام',
      'agency_staff': '🏢 موظف وكالة'
    };
    return roleMap[role] || role;
  };

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency ? `${agency.name} - ${agency.city}` : 'غير محدد';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">👤 {t('userManagement')}</h2>
            <p className="text-gray-600 mt-1">إدارة وتنظيم المستخدمين والصلاحيات</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 hover:bg-brand-700">
                <Plus className="h-4 w-4 mr-2" />
                {t('addUser')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingUser ? '✏️ تعديل المستخدم' : '➕ إضافة مستخدم جديد'}
                </DialogTitle>
                <DialogDescription>
                  {editingUser ? 'قم بتعديل بيانات المستخدم' : 'أدخل بيانات المستخدم الجديد'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">👤 الاسم الكامل</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="أدخل الاسم الكامل"
                    required
                    className="text-right"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">📧 البريد الإلكتروني</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@domain.com"
                    required
                    className="text-right"
                  />
                </div>
                
                <div>
                  <Label htmlFor="password">🔒 كلمة المرور {editingUser && '(اتركها فارغة للاحتفاظ بالحالية)'}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? 'اتركها فارغة لعدم التغيير' : 'أدخل كلمة المرور'}
                    required={!editingUser}
                    className="text-right"
                  />
                </div>
                
                <div>
                  <Label htmlFor="role">🎭 الدور الوظيفي</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الدور" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">👑 مدير عام</SelectItem>
                      <SelectItem value="general_accountant">💼 محاسب عام</SelectItem>
                      <SelectItem value="services_manager">📦 مسؤول الخدمات</SelectItem>
                      <SelectItem value="general_manager">🎯 مسؤول عام (أفواج/طيران/موردين)</SelectItem>
                      <SelectItem value="agency_staff">🏢 موظف وكالة</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="agency_id">🏢 الوكالة</Label>
                  <Select value={formData.agency_id} onValueChange={(value) => setFormData({ ...formData, agency_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الوكالة" />
                    </SelectTrigger>
                    <SelectContent>
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id}>
                          {agency.name} - {agency.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="bg-brand-600 hover:bg-brand-700">
                    {editingUser ? '💾 تحديث' : '➕ إضافة'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-r from-brand-50 to-brand-50 border-brand-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-medium text-brand-800">إجمالي المستخدمين</p>
                <p className="text-2xl font-bold text-brand-900">{users.length}</p>
              </div>
              <Users className="h-8 w-8 text-brand-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-medium text-purple-800">المديرين</p>
                <p className="text-2xl font-bold text-purple-900">
                  {users.filter(u => u.role === 'super_admin').length}
                </p>
              </div>
              <Settings className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-medium text-green-800">المحاسبين</p>
                <p className="text-2xl font-bold text-green-900">
                  {users.filter(u => u.role === 'general_accountant').length}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-medium text-orange-800">موظفي الوكالات</p>
                <p className="text-2xl font-bold text-orange-900">
                  {users.filter(u => u.role === 'agency_staff').length}
                </p>
              </div>
              <Building2 className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 ml-2" />
            قائمة المستخدمين
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">👤 الاسم</TableHead>
                <TableHead className="text-right">📧 البريد الإلكتروني</TableHead>
                <TableHead className="text-right">🎭 الدور</TableHead>
                <TableHead className="text-right">🏢 الوكالة</TableHead>
                <TableHead className="text-right">📅 تاريخ الإنشاء</TableHead>
                <TableHead className="text-right">⚙️ الإجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium text-right">{user.name}</TableCell>
                  <TableCell className="text-right">{user.email}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={user.role === 'super_admin' ? 'default' : 'secondary'}>
                      {getRoleDisplay(user.role)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{getAgencyName(user.agency_id)}</TableCell>
                  <TableCell className="text-right">
                    {formatDateWithEnglishNumerals(user.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        className="text-brand-600 hover:text-brand-700"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {user.id !== currentUser.id && ( // Can't delete self
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(user.id)}
                          className="text-red-600 hover:text-red-700"
                          title="حذف المستخدم"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};



export default UserManagement;
